import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@material-ui/core";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import PrintOutlinedIcon from "@material-ui/icons/PrintOutlined";
import { i18n } from "../../translate/i18n";
import { books, labels, normalizeLanguage, searchText, screenshotUrl } from "./data";
import "./manual.css";

function Screenshot({ chapter, language, onEnlarge }) {
  const [failed, setFailed] = useState(false);
  const t = labels[language];
  const src = screenshotUrl(chapter, language);
  useEffect(() => setFailed(false), [src]);
  return <figure className="manual-figure">
    {failed ? <p role="status">{t.imageError}</p> : <button className="manual-image-button" type="button" onClick={() => onEnlarge({ src, title: chapter.title })} aria-label={`${t.enlarge}: ${chapter.title}`}>
      <img src={src} alt={chapter.title} width="1440" height="1000" onError={() => setFailed(true)} />
      <span className="manual-enlarge">{t.enlarge} ↗</span>
    </button>}
    <figcaption>{t.screenshot}</figcaption>
  </figure>;
}

export default function Manual() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [language, setLanguage] = useState(() => normalizeLanguage(params.get("lang") || localStorage.getItem("manualLanguage") || i18n.language));
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");
  const [image, setImage] = useState(null);
  const articleRef = useRef(null);
  const t = labels[language];
  const chapters = books[language];
  const id = params.get("chapter") || chapters[0].id;
  const term = searchText(query.trim());
  const visible = chapters.filter(chapter => (group === "all" || chapter.group === group) && searchText([chapter.title, chapter.intro, ...chapter.steps, chapter.tip, t.groups[chapter.group]].join(" ")).includes(term));
  const chapter = visible.find(item => item.id === id) || visible[0];
  const currentIndex = chapter ? visible.indexOf(chapter) : -1;

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("lang");
    if (requested) setLanguage(normalizeLanguage(requested));
  }, [location.search]);
  useEffect(() => { articleRef.current?.scrollTo?.(0, 0); }, [chapter?.id, language]);
  useEffect(() => {
    const original = document.title;
    document.title = `${t.title} • ${chapter?.title || t.contents}`;
    return () => { document.title = original; };
  }, [t.title, t.contents, chapter?.title]);

  const selectChapter = (next) => history.push({ pathname: "/manual", search: `?lang=${language}&chapter=${next.id}` });
  const changeLanguage = (event) => {
    const next = event.target.value;
    setLanguage(next);
    localStorage.setItem("manualLanguage", next);
    setQuery("");
    history.replace({ pathname: "/manual", search: `?lang=${next}&chapter=${chapter?.id || "start"}` });
  };

  return <section className="manual-page" lang={language === "pt" ? "pt-BR" : language} aria-label={t.title}>
    <header className="manual-header">
      <div className="manual-heading"><MenuBookOutlinedIcon /><div><h1>{t.title}</h1><p>{t.subtitle}</p></div></div>
      <div className="manual-toolbar">
        <TextField id="manual-language" select label={t.language} variant="outlined" size="small" value={language} onChange={changeLanguage}>
          <MenuItem value="pt">Português (Brasil)</MenuItem><MenuItem value="es">Español</MenuItem><MenuItem value="en">English</MenuItem>
        </TextField>
        <Button component="a" variant="contained" color="primary" href={`${process.env.PUBLIC_URL || ""}/manual/manual-${language}.pdf`} download={`manual-${language}.pdf`} startIcon={<GetAppIcon />}>{t.download}</Button>
      </div>
    </header>
    <p className="manual-notice">{t.notice}</p>
    <div className="manual-workspace">
      <aside className="manual-sidebar">
        <TextField id="manual-search" label={t.search} value={query} onChange={event => setQuery(event.target.value)} variant="outlined" size="small" fullWidth type="search" />
        <TextField id="manual-group" select label={t.contents} value={group} onChange={event => setGroup(event.target.value)} variant="outlined" size="small" fullWidth>
          <MenuItem value="all">{t.all}</MenuItem>
          {Object.entries(t.groups).map(([value, title]) => <MenuItem key={value} value={value}>{title}</MenuItem>)}
        </TextField>
        <p className="manual-count" role="status">{visible.length} / {chapters.length} {t.chapters}</p>
        <nav aria-label={t.contents} className="manual-toc">
          {visible.map(item => <button type="button" key={item.id} onClick={() => selectChapter(item)} aria-current={chapter?.id === item.id ? "page" : undefined}>
            <small>{t.groups[item.group]}</small><span>{item.title}</span>
          </button>)}
        </nav>
      </aside>
      <div className="manual-reader" ref={articleRef}>
        {chapter ? <article className="manual-article" key={chapter.id}>
          <div className="manual-chapter-heading"><span className="manual-category">{t.groups[chapter.group]}</span><Button size="small" className="manual-print" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>{t.print}</Button></div>
          <h2>{chapter.title}</h2><p className="manual-intro">{chapter.intro}</p>
          <p className="manual-path">{t.path}: {t.groups[chapter.group]} → {chapter.title}</p>
          <Screenshot chapter={chapter} language={language} onEnlarge={setImage} />
          <h3>{t.steps}</h3>
          <ol className="manual-steps">{chapter.steps.map((step, index) => <li key={index}>{step}</li>)}</ol>
          <div className="manual-tip"><strong>{t.tip}</strong><p>{chapter.tip}</p></div>
          <footer className="manual-footer"><span>{t.updated}</span><div>
            <Button disabled={currentIndex <= 0} onClick={() => selectChapter(visible[currentIndex - 1])}>← {t.previous}</Button>
            <Button disabled={currentIndex >= visible.length - 1} onClick={() => selectChapter(visible[currentIndex + 1])}>{t.next} →</Button>
          </div></footer>
        </article> : <div className="manual-empty" role="status"><p>{t.empty}</p><Button onClick={() => { setQuery(""); setGroup("all"); }}>{t.clear}</Button></div>}
      </div>
    </div>
    <Dialog open={Boolean(image)} onClose={() => setImage(null)} maxWidth="xl" fullWidth aria-labelledby="manual-image-title">
      <DialogTitle id="manual-image-title">{image?.title}</DialogTitle>
      <DialogContent>{image && <img src={image.src} alt={image.title} style={{ width: "100%", height: "auto" }} />}</DialogContent>
      <DialogActions><Button onClick={() => setImage(null)} color="primary">{t.close}</Button></DialogActions>
    </Dialog>
  </section>;
}
